import { LitEntity, css, svg, html, nothing } from '../../utility/lit-entity';
import './bin-profile';
import { SCALE } from './bin-profile-svg';

class BinProfileMeasurements extends LitEntity {
  constructor() {
    super();
    this.pipLength = 3;
    this.measurementMargin = 4;
    this.padding = 2;
  }

  static get properties() {
    return {
      padding: { type: Number },
      pipLength: { type: Number, attribute: 'pip-length' },
      measurementMargin: { type: Number, attribute: 'measurement-margin' },
    };
  }

  static get styles() {
    return css`
      :host {
        position: relative;
        display: block;
      }
      svg, bin-profile {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }
      .measurement {
        transition: opacity .2s;
      }
      .measurement.top-height {
        opacity: var(--bin-profile-measurements-top-opacity, 0);
      }
      .measurement.body-height {
        opacity: var(--bin-profile-measurements-body-opacity, 0);
      }
      .measurement.hopper-height {
        opacity: var(--bin-profile-measurements-hopper-opacity, 0);
      }
      .measurement.ring-count {
        opacity: var(--bin-profile-measurements-ring-opacity, 0);
      }
      .measurement.diameter {
        opacity: var(--bin-profile-measurements-diameter-opacity, 0);
      }
      .measurement-line {
        stroke: var(--bin-profile-measurement-colour, var(--black-base));
        stroke-width: 1px;
        stroke-dasharray: 2px 2px;
      }
      .pip {
        stroke: var(--bin-profile-measurement-colour, var(--black-base));
        stroke-width: 1px;
      }
    `;
  }

  render() {
    if (!this.entity) {
      return nothing;
    }

    this.profilePadding = this.measurementMargin + this.padding + this.pipLength;
    this.doubleProfilePadding = this.profilePadding * 2;

    return html`
      ${this._renderSVG()}
      <bin-profile
        .href="${this.href}"
        .padding="${this.profilePadding}"
        additional-leg-height="1"
      /></bin-profile>
    `;
  }

  _renderSVG() {
    const { totalHeight, body } = this.getProperty('dimensions');
    this.width = (body.radius * 2 * SCALE) + this.doubleProfilePadding;
    this.height = (totalHeight * SCALE) + this.doubleProfilePadding;

    this.leftOrigin = this.padding + (this.pipLength / 2);
    this.rightOrigin = this.width - (this.padding + (this.pipLength / 2));

    return svg`
      <svg
        viewBox="0 0 ${this.width} ${this.height}"
        width="100%"
        height="100%"
      >
        ${this._drawTopHeight()}
        ${this._drawBodyHeight()}
        ${this._drawHopperHeight()}
        ${this._drawRingCount()}
        ${this._drawDiameter()}
      </svg>
    `;
  }

  _drawTopHeight() {
    const x = this.rightOrigin;
    const y = this.profilePadding;
    const { top } = this.getProperty('dimensions');
    const length = top.height * SCALE;

    return this._drawVerticalMeasurement({ className: 'top-height', x, y, length });
  }

  _drawBodyHeight() {
    const x = this.rightOrigin;
    const { body, top } = this.getProperty('dimensions');
    const y = (top.height * SCALE) + this.profilePadding;
    const length = body.height * SCALE;

    return this._drawVerticalMeasurement({ className: 'body-height', x, y, length });
  }

  _drawHopperHeight() {
    const x = this.rightOrigin;
    const { hopper } = this.getProperty('dimensions');
    const length = hopper.height * SCALE;
    const y = this.height - length - this.profilePadding;

    return this._drawVerticalMeasurement({ className: 'hopper-height', x, y, length });
  }

  _drawRingCount() {
    const x = this.leftOrigin;
    const { body, top, ringCount } = this.getProperty('dimensions');
    const y = (top.height * SCALE) + this.profilePadding;
    const length = body.height * SCALE;

    return this._drawVerticalMeasurement({ className: 'ring-count', x, y, length, pipCount: ringCount - 1 });
  }

  _drawDiameter() {
    const x = this.profilePadding;
    const { body } = this.getProperty('dimensions');
    const y = this.height - this.padding - (this.pipLength / 2);
    const length = body.radius * 2 * SCALE;

    return this._drawHorizontalMeasurement({ className: 'diameter', x, y, length });
  }

  _drawHorizontalMeasurement({ className, x, y, length, pipCount }) {
    const extraPips = [];
    const pipDelta = length / (pipCount + 1);
    for (let i = 1; i < pipCount + 1; i++) {
      extraPips.push(this._drawPipVertical({ x: x + (pipDelta * i), y }));
    }

    return svg`
      <g class="measurement ${className}">
        ${this._drawPipVertical({ x, y })}
        <line class="measurement-line" x1="${x}" y1="${y}" x2="${x + length}" y2="${y}"></line>
        ${this._drawPipVertical({ x: x + length, y })}
        ${extraPips}
      </g>
    `;
  }

  _drawVerticalMeasurement({ className, x, y, length, pipCount }) {
    const extraPips = [];
    const pipDelta = length / (pipCount + 1);
    for (let i = 1; i < pipCount + 1; i++) {
      extraPips.push(this._drawPipHorizontal({ x, y: y + (pipDelta * i) }));
    }

    return svg`
      <g class="measurement ${className}">
        ${this._drawPipHorizontal({ x, y })}
        <line class="measurement-line" x1="${x}" y1="${y}" x2="${x}" y2="${y + length}"></line>
        ${this._drawPipHorizontal({ x, y: y + length })}
        ${extraPips}
      </g>
    `;
  }

  _drawPipHorizontal({ x, y }) {
    return svg`
      <line class="pip" x1="${x - this.pipLength}" y1="${y}" x2="${x + this.pipLength}" y2="${y}"></line>
    `;
  }

  _drawPipVertical({ x, y }) {
    return svg`
      <line class="pip" x1="${x}" y1="${y - this.pipLength}" x2="${x}" y2="${y + this.pipLength}"></line>
    `;
  }
}

window.customElements.define('bin-profile-measurements', BinProfileMeasurements);
